import React, { useState, useEffect, useCallback } from 'react';
import {
    collection,
    query,
    getDocs,
    orderBy
} from 'firebase/firestore';
import { db } from '../../../../Services/firebase-config';
import { useNavigate } from 'react-router-dom';
import {
    UserGroupIcon,
    CalendarIcon,
    EyeIcon,
    SearchIcon
} from '@heroicons/react/outline';

const TeamsManagement = () => {
    const [teams, setTeams] = useState([]);
    const [filteredTeams, setFilteredTeams] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [mentorFilter, setMentorFilter] = useState('all'); // New state for mentor filter
    const [searchLoading, setSearchLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [teamsPerPage, setTeamsPerPage] = useState(10);
    const navigate = useNavigate();

    useEffect(() => {
        fetchTeams();
    }, []);

    // Combined filter effect for search and mentor filter
    useEffect(() => {
        applyFilters();
        setCurrentPage(1);
    }, [searchTerm, mentorFilter, teams]);

    // Reset to page 1 when teamsPerPage changes
    useEffect(() => {
        setCurrentPage(1);
    }, [teamsPerPage]);

    const fetchTeams = async () => {
        try {
            setLoading(true);
            const teamsRef = collection(db, 'hackathonTeams');
            const q = query(teamsRef, orderBy('createdAt', 'desc'));
            const querySnapshot = await getDocs(q);

            const teamsData = [];
            querySnapshot.forEach((doc) => {
                teamsData.push({
                    id: doc.id,
                    ...doc.data()
                });
            });

            setTeams(teamsData);
            setFilteredTeams(teamsData);
        } catch (error) {
            console.error('Error fetching teams:', error);
        } finally {
            setLoading(false);
        }
    };

    const applyFilters = useCallback(() => {
        setSearchLoading(true);
        
        const delayedFilter = setTimeout(() => {
            let results = [...teams];

            // Apply search filter
            if (searchTerm.trim()) {
                const searchLower = searchTerm.toLowerCase().trim();
                results = results.filter(team => 
                    team.name?.toLowerCase().includes(searchLower) ||
                    team.description?.toLowerCase().includes(searchLower)
                );
            }

            // Apply mentor filter
            if (mentorFilter === 'with-mentor') {
                results = results.filter(team => team.mentor || team.mentorId);
            } else if (mentorFilter === 'without-mentor') {
                results = results.filter(team => !team.mentor && !team.mentorId);
            }

            setFilteredTeams(results);
            setSearchLoading(false);
        }, searchTerm.trim() ? 300 : 0); // Only debounce for search, not filter

        return () => {
            clearTimeout(delayedFilter);
            setSearchLoading(false);
        };
    }, [teams, searchTerm, mentorFilter]);

    const formatDate = (timestamp) => {
        if (!timestamp) return 'N/A';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const getMentorStatus = (team) => {
        if (team.mentor || team.mentorId) {
            return {
                status: 'assigned',
                mentorName: team.mentor?.name || team.mentor?.email || 'Mentor assigned',
                className: 'bg-green-100 text-green-800'
            };
        }
        return {
            status: 'unassigned',
            mentorName: 'No mentor assigned',
            className: 'bg-red-100 text-red-800'
        };
    };

    // Pagination logic
    const indexOfLastTeam = currentPage * teamsPerPage;
    const indexOfFirstTeam = indexOfLastTeam - teamsPerPage;
    const currentTeams = filteredTeams.slice(indexOfFirstTeam, indexOfLastTeam);
    const totalPages = Math.ceil(filteredTeams.length / teamsPerPage);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleTeamsPerPageChange = (newTeamsPerPage) => {
        setTeamsPerPage(newTeamsPerPage);
        setCurrentPage(1);
    };

    const handleMentorFilterChange = (newFilter) => {
        setMentorFilter(newFilter);
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-custom-blue-1"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Teams Management</h1>
                <p className="text-gray-600">Manage hackathon teams and assign mentors</p>
            </div>

            {/* Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
                <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Total Teams</p>
                            <p className="text-3xl font-bold text-custom-blue-1 mt-2">{teams.length}</p>
                        </div>
                        <div className="p-3 rounded-lg bg-blue-50">
                            <UserGroupIcon className="h-8 w-8 text-custom-blue-1" />
                        </div>
                    </div>
                </div>
                <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">With Mentors</p>
                            <p className="text-3xl font-bold text-custom-green-1 mt-2">
                                {teams.filter(team => team.mentor || team.mentorId).length}
                            </p>
                        </div>
                        <div className="p-3 rounded-lg bg-green-50">
                            <svg className="h-8 w-8 text-custom-green-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                    </div>
                </div>
                <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Unassigned</p>
                            <p className="text-3xl font-bold text-orange-500 mt-2">
                                {teams.filter(team => !team.mentor && !team.mentorId).length}
                            </p>
                        </div>
                        <div className="p-3 rounded-lg bg-orange-50">
                            <svg className="h-8 w-8 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                    </div>
                </div>
                <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Total Members</p>
                            <p className="text-3xl font-bold text-purple-600 mt-2">
                                {teams.reduce((sum, team) => sum + (team.members?.length || 0), 0)}
                            </p>
                        </div>
                        <div className="p-3 rounded-lg bg-purple-50">
                            <svg className="h-8 w-8 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                        </div>
                    </div>
                </div>
            </div>

            {/* Search Bar and Filter */}
            <div className="mb-6 flex flex-col sm:flex-row gap-4">
                {/* Search Bar */}
                <div className="relative flex-1 max-w-md">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <SearchIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                        type="text"
                        placeholder="Search teams by name or description..."
                        className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-custom-blue-1 focus:border-custom-blue-1 disabled:bg-gray-100 disabled:cursor-not-allowed"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        disabled={loading}
                    />
                    {searchLoading && (
                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-custom-blue-1"></div>
                        </div>
                    )}
                </div>

                {/* Mentor Filter Dropdown */}
                <div className="relative">
                    <select
                        value={mentorFilter}
                        onChange={(e) => handleMentorFilterChange(e.target.value)}
                        className="block w-full pl-3 pr-10 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-1 focus:ring-custom-blue-1 focus:border-custom-blue-1 disabled:bg-gray-100 disabled:cursor-not-allowed"
                        disabled={loading}
                    >
                        <option value="all">All Teams</option>
                        <option value="with-mentor">Teams with Mentors</option>
                        <option value="without-mentor">Teams without Mentors</option>
                    </select>
                </div>
            </div>

            {/* Results Summary and Items Per Page */}
            <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <p className="text-sm text-gray-600">
                        {searchLoading ? (
                            <span className="flex items-center">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-custom-blue-1 mr-2"></div>
                                Filtering...
                            </span>
                        ) : (
                            <>
                                Showing {currentTeams.length > 0 ? ((currentPage - 1) * teamsPerPage) + 1 : 0} to{' '}
                                {Math.min(currentPage * teamsPerPage, Math.min(filteredTeams.length, ((currentPage - 1) * teamsPerPage) + currentTeams.length))} of {filteredTeams.length} teams
                                {(searchTerm.trim() || mentorFilter !== 'all') && ` (filtered from ${teams.length} total)`}
                            </>
                        )}
                    </p>
                </div>

                <div className="flex items-center gap-2">
                    <label htmlFor="itemsPerPage" className="text-sm text-gray-600">
                        Show:
                    </label>
                    <select
                        id="itemsPerPage"
                        value={teamsPerPage}
                        onChange={(e) => handleTeamsPerPageChange(Number(e.target.value))}
                        className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-custom-blue-1 focus:border-custom-blue-1"
                        disabled={searchLoading}
                    >
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                    </select>
                    <span className="text-sm text-gray-600">per page</span>
                </div>
            </div>

            {/* Teams Table */}
            <div className="bg-white shadow-sm overflow-hidden rounded-lg border">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Team Details
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Members
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Mentor Status
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Created Date
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {searchLoading ? (
                                <tr>
                                    <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                                        <div className="flex items-center justify-center">
                                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-custom-blue-1 mr-2"></div>
                                            Filtering teams...
                                        </div>
                                    </td>
                                </tr>
                            ) : currentTeams.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                                        {searchTerm.trim() || mentorFilter !== 'all' ? 'No teams found matching your filters' : 'No teams found'}
                                    </td>
                                </tr>
                            ) : (
                                currentTeams.map((team) => {
                                    const mentorStatus = getMentorStatus(team);
                                    return (
                                        <tr key={team.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div>
                                                    <div className="text-sm font-medium text-gray-900">
                                                        {team.name}
                                                    </div>
                                                    <div className="text-sm text-gray-500 max-w-xs truncate">
                                                        {team.description}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center">
                                                    <UserGroupIcon className="h-5 w-5 text-gray-400 mr-2" />
                                                    <span className="text-sm text-gray-900">
                                                        {team.members?.length || 0} / {team.maxMembers || 5}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${mentorStatus.className}`}>
                                                    {mentorStatus.mentorName}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                <div className="flex items-center">
                                                    <CalendarIcon className="h-4 w-4 mr-1" />
                                                    {formatDate(team.createdAt)}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                <button
                                                    onClick={() => navigate(`/eventdashboard/team-details/${team.id}`, {
                                                        state: { team }
                                                    })}
                                                    className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-indigo-600 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                                >
                                                    <EyeIcon className="h-4 w-4 mr-1" />
                                                    View Details
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="mt-6 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Previous
                        </button>

                        <div className="flex items-center gap-1">
                            {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                                let pageNumber;
                                if (totalPages <= 7) {
                                    pageNumber = i + 1;
                                } else if (currentPage <= 4) {
                                    pageNumber = i + 1;
                                } else if (currentPage >= totalPages - 3) {
                                    pageNumber = totalPages - 6 + i;
                                } else {
                                    pageNumber = currentPage - 3 + i;
                                }

                                if (pageNumber < 1 || pageNumber > totalPages) return null;

                                return (
                                    <button
                                        key={pageNumber}
                                        onClick={() => handlePageChange(pageNumber)}
                                        className={`px-3 py-2 text-sm font-medium rounded-md ${pageNumber === currentPage
                                            ? 'bg-custom-blue-1 text-white'
                                            : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                                            }`}
                                    >
                                        {pageNumber}
                                    </button>
                                );
                            })}
                        </div>

                        <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Next
                        </button>
                    </div>

                    <div className="text-sm text-gray-600">
                        Page {currentPage} of {totalPages}
                    </div>
                </div>
            )}
        </div>
    );
};

export default TeamsManagement;